import React, { useMemo, useRef, useEffect } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';

import boost from 'highcharts/modules/boost';
// @ts-ignore - boost module factory call
if (typeof boost === 'function') (boost as any)(Highcharts);

interface CombinedChartProps {
  priceHistoryCsv: string;
  salesRankCsv: string;
  currencySymbol?: string;
  height?: number;
}

const BUYBOX_COLOR = '#ff00ff';
const RANK_COLOR = '#f59e0b';

function parseTimestamp(dateStr: string): number {
  const d = new Date(dateStr.replace(' ', 'T'));
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

function parsePriceCsv(csv: string): [number, number][] {
  if (!csv) return [];
  const lines = csv.trim().split('\n');
  if (lines.length < 2) return [];
  const out: [number, number][] = [];
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split(',');
    if (parts.length < 2) continue;
    const ts = parseTimestamp(parts[0].trim());
    const price = parseFloat(parts[1]);
    if (ts && !isNaN(price) && price > 0) out.push([ts, price]);
  }
  return out.sort((a, b) => a[0] - b[0]);
}

function parseRankCsv(csv: string): [number, number][] {
  if (!csv) return [];
  const lines = csv.trim().split('\n');
  if (lines.length < 2) return [];
  const out: [number, number][] = [];
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split(',');
    if (parts.length < 2) continue;
    const ts = parseTimestamp(parts[0].trim());
    const rank = parseInt(parts[1], 10);
    if (ts && !isNaN(rank) && rank > 0) out.push([ts, rank]);
  }
  return out.sort((a, b) => a[0] - b[0]);
}

function formatRank(rank: number): string {
  if (rank >= 1_000_000) return `${(rank / 1_000_000).toFixed(1)}M`;
  if (rank >= 1_000) return `${(rank / 1_000).toFixed(0)}K`;
  return rank.toLocaleString();
}

// Apply dark theme globally once
Highcharts.setOptions({
  chart: {
    backgroundColor: 'transparent',
    style: { fontFamily: 'inherit' },
  },
  title: { style: { color: '#e8ecf4' } },
  legend: {
    itemStyle: { color: '#8b93a1', fontSize: '12px', fontWeight: '600' },
    itemHoverStyle: { color: '#7c6aef' },
    itemHiddenStyle: { color: '#3a3e48', textDecoration: 'line-through' },
  },
  xAxis: {
    gridLineColor: 'rgba(255,255,255,0.05)',
    labels: { style: { color: '#6b7280', fontSize: '11px' } },
    lineColor: '#1e1e2a',
    tickColor: '#1e1e2a',
    crosshair: {
      color: 'rgba(124, 106, 239, 0.15)',
      dashStyle: 'Solid' as Highcharts.DashStyleValue,
      width: 1,
    },
  },
  yAxis: {
    gridLineColor: 'rgba(255,255,255,0.05)',
    labels: { style: { color: '#6b7280', fontSize: '11px' } },
  },
  tooltip: {
    backgroundColor: 'rgba(255, 255, 255, 0.88)',
    borderColor: 'rgba(0, 0, 0, 0.08)',
    borderRadius: 10,
    borderWidth: 1,
    shadow: {
      offsetX: 0,
      offsetY: 4,
      width: 16,
      color: 'rgba(0,0,0,0.10)',
    },
    style: { color: '#1a1d24', fontSize: '12px' },
    padding: 12,
    useHTML: true,
  },
  navigator: { enabled: false },
  rangeSelector: {
    buttonTheme: {
      fill: '#141419',
      stroke: '#1e1e2a',
      'stroke-width': 1,
      r: 6,
      style: { color: '#6b7280', fontWeight: '500', fontSize: '12px' },
      states: {
        hover: { fill: '#1a1a22', stroke: '#7c6aef', style: { color: '#e8ecf4' } },
        select: { fill: '#7c6aef', stroke: '#7c6aef', style: { color: '#ffffff', fontWeight: '600' } },
      },
    },
    inputStyle: {
      backgroundColor: '#141419',
      color: '#e8ecf4',
      borderRadius: 6,
    },
    inputBoxBorderColor: '#2a2a3a',
    labelStyle: { color: '#6b7280', fontWeight: '500', fontSize: '12px' },
  },
  scrollbar: { enabled: false },
});

const CombinedChart: React.FC<CombinedChartProps> = ({ priceHistoryCsv, salesRankCsv, currencySymbol = '$', height = 400 }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const priceData = useMemo(() => parsePriceCsv(priceHistoryCsv), [priceHistoryCsv]);
  const rankData = useMemo(() => parseRankCsv(salesRankCsv), [salesRankCsv]);

  const hasPrice = priceData.length >= 2;
  const hasRank = rankData.length >= 2;

  // Reflow on container resize
  useEffect(() => {
    const timer = setTimeout(() => {
      chartRef.current?.chart?.reflow();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const options = useMemo<Highcharts.Options>(() => {
    const series: Highcharts.SeriesOptionsType[] = [];

    if (hasPrice) {
      series.push({
        type: 'line',
        name: 'Buy Box Price',
        data: priceData,
        step: 'left',
        color: BUYBOX_COLOR,
        yAxis: 0,
        lineWidth: 2,
        tooltip: {
          pointFormatter: function (this: Highcharts.Point) {
            return `<span style="color:${BUYBOX_COLOR}">\u25CF</span> Buy Box: <b>${currencySymbol}${this.y!.toFixed(2)}</b><br/>`;
          },
        },
        marker: { enabled: false, states: { hover: { enabled: true, radius: 5 } } },
      });
    }

    if (hasRank) {
      series.push({
        type: 'line',
        name: 'Sales Rank',
        data: rankData,
        step: 'left',
        color: RANK_COLOR,
        yAxis: hasPrice ? 1 : 0,
        lineWidth: 2,
        tooltip: {
          pointFormatter: function (this: Highcharts.Point) {
            return `<span style="color:${RANK_COLOR}">\u25CF</span> Sales Rank: <b>#${Number(this.y).toLocaleString()}</b><br/>`;
          },
        },
        marker: { enabled: false, states: { hover: { enabled: true, radius: 5 } } },
      });
    }

    const yAxis: Highcharts.YAxisOptions[] = [];

    if (hasPrice) {
      yAxis.push({
        title: { text: 'Buy Box Price', style: { color: BUYBOX_COLOR } },
        labels: {
          formatter: function () {
            return currencySymbol + (this.value as number).toFixed(2);
          },
          style: { color: BUYBOX_COLOR },
        },
        opposite: false,
        gridLineColor: 'rgba(255,255,255,0.06)',
      });
    }

    if (hasRank) {
      yAxis.push({
        title: { text: 'Sales Rank', style: { color: RANK_COLOR } },
        labels: {
          formatter: function () {
            return '#' + formatRank(this.value as number);
          },
          style: { color: RANK_COLOR },
        },
        opposite: hasPrice,
        reversed: true,
        gridLineColor: hasPrice ? 'transparent' : 'rgba(255,255,255,0.06)',
      });
    }

    // Determine initial range — default to 3 months
    const allTimestamps = [...priceData.map(d => d[0]), ...rankData.map(d => d[0])];
    const maxTs = allTimestamps.length > 0 ? Math.max(...allTimestamps) : Date.now();
    const minTs = allTimestamps.length > 0 ? Math.min(...allTimestamps) : Date.now();
    const threeMonthsAgo = maxTs - 90 * 24 * 60 * 60 * 1000;
    const defaultMin = Math.max(minTs, threeMonthsAgo);

    return {
      chart: {
        height,
        zooming: { type: 'x' },
        panning: { enabled: true, type: 'x' },
        panKey: 'shift',
      },
      title: { text: undefined },
      boost: {
        useGPUTranslations: true,
        seriesThreshold: 1,
      },
      rangeSelector: {
        selected: undefined,
        buttons: [
          { type: 'week', count: 1, text: '1W' },
          { type: 'month', count: 1, text: '1M' },
          { type: 'month', count: 3, text: '3M' },
          { type: 'month', count: 6, text: '6M' },
          { type: 'year', count: 1, text: '1Y' },
          { type: 'all', text: 'All' },
        ],
        inputEnabled: true,
        inputDateFormat: '%Y-%m-%d',
        inputEditDateFormat: '%Y-%m-%d',
      },
      xAxis: {
        type: 'datetime',
        min: defaultMin,
        max: maxTs,
        crosshair: true,
      },
      yAxis,
      tooltip: {
        shared: true,
        split: false,
        xDateFormat: '%b %d, %Y %H:%M',
        headerFormat: '<div style="font-size:11px;color:#6b7280;margin-bottom:6px;padding-bottom:5px;border-bottom:1px solid rgba(0,0,0,0.08)">{point.key}</div>',
        footerFormat: '',
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        symbolRadius: 4,
        itemDistance: 24,
        padding: 12,
      },
      navigator: {
        enabled: false,
      },
      plotOptions: {
        series: {
          animation: false,
          turboThreshold: 0,
          states: {
            hover: { lineWidthPlus: 1 },
          },
        },
      },
      series,
      credits: { enabled: false },
    };
  }, [priceData, rankData, hasPrice, hasRank, height]);

  if (!hasPrice && !hasRank) {
    return (
      <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 16, textAlign: 'center' }}>
        Not enough data points for a chart.
      </div>
    );
  }

  // Summary stats
  const currentPrice = hasPrice ? priceData[priceData.length - 1][1] : null;
  const firstPrice = hasPrice ? priceData[0][1] : null;
  const priceDelta = currentPrice != null && firstPrice != null ? currentPrice - firstPrice : null;
  const pricePct = firstPrice && priceDelta != null ? ((priceDelta / firstPrice) * 100).toFixed(1) : null;

  const currentRank = hasRank ? rankData[rankData.length - 1][1] : null;
  const firstRank = hasRank ? rankData[0][1] : null;
  const rankDelta = currentRank != null && firstRank != null ? currentRank - firstRank : null;

  return (
    <div className="price-chart-container">
      <div className="price-chart-header" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        {hasPrice && (
          <div>
            <span className="price-chart-current" style={{ color: BUYBOX_COLOR }}>
              Buy Box: {currencySymbol}{currentPrice!.toFixed(2)}
            </span>
            {priceDelta != null && (
              <span className={`price-chart-delta ${priceDelta >= 0 ? 'up' : 'down'}`}>
                {priceDelta >= 0 ? '+' : ''}{currencySymbol}{Math.abs(priceDelta).toFixed(2)} ({priceDelta >= 0 ? '+' : ''}{pricePct}%)
              </span>
            )}
          </div>
        )}
        {hasRank && (
          <div style={{ textAlign: 'right' }}>
            <span className="price-chart-current" style={{ color: RANK_COLOR }}>
              Rank: #{currentRank!.toLocaleString()}
            </span>
            {rankDelta != null && (
              <span className={`price-chart-delta ${rankDelta < 0 ? 'down' : 'up'}`} style={{ marginLeft: '6px' }}>
                {rankDelta < 0
                  ? `\u2191 improved by ${Math.abs(rankDelta).toLocaleString()}`
                  : rankDelta > 0
                  ? `\u2193 dropped by ${rankDelta.toLocaleString()}`
                  : 'no change'}
              </span>
            )}
          </div>
        )}
      </div>

      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={options}
        ref={chartRef}
      />

      <div className="price-chart-footer" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <span>
          {hasPrice && <span>{priceData.length} price points</span>}
          {hasPrice && hasRank && <span> &middot; </span>}
          {hasRank && <span>{rankData.length} rank points</span>}
          {hasRank && <span style={{ color: 'var(--text-muted)' }}> &middot; Lower rank = better</span>}
        </span>
        <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
          Drag to zoom &middot; Shift+drag to pan &middot; Click legend to toggle
        </span>
      </div>
    </div>
  );
};

export default React.memo(CombinedChart);
